import { type FC, type ReactNode, useEffect, useState } from 'react';
import cx from 'classnames';
import { useUnit } from 'effector-react';
import styles from './MenuOverlay.module.css';
import { Button } from '../Button';
import { UnstyledButton } from '../UnstyledButton';
import CloseIcon from '../../icons/close.svg?react';
import MapTrifoldIcon from '../../icons/map_trifold.svg?react';
import { useI18n, formatMessage } from '../../i18n';
import { useLayoutMode } from '../../hooks/useLayoutMode';
import { $sceneProgressByPoiFilterId, type SceneProgress } from '../../state/visitedSights';
import {
  $hajjJourneyProgress,
  $historicalJourneyProgress,
  $visitedInsideScenes,
  MENU_HISTORICAL_SLIDES,
  MENU_MAIN_SCENE_URL_IDS,
  type MenuPoiProgress,
} from '../../state/sceneProgress';
import { SCENE_POI_ID } from '../../scene/sceneNavigationMap';
import { getInsideScenesForParent } from '../../scene/insideScenes/registry';
import { isEraScene, isStubEraScene } from '../../scene/eraScenes/registry';
import {
  menuSceneSelected,
  menuTabSwitched,
  type MenuTab,
} from '../../analytics/menuTracker';
import { MenuProgressHeader } from './MenuProgressHeader';
import { MenuSceneCard, type InsideSceneItem } from './MenuSceneCard';

type MenuOverlayProps = {
  open: boolean;
  onClose: () => void;
  /** Switch to a main scene (URL-id: kaaba / mina / muzdalifah / arafat). */
  onEnterMainScene: (urlId: string) => void;
  /** Enter an inside-scene (canonical id, e.g. `kaaba-inside`). */
  onEnterInsideScene: (insideId: string) => void;
  /** Enter a historical era scene (e.g. `kaaba-600ce`). */
  onEnterHistoricalScene: (eraId: string) => void;
  /** Return to the root map (overall). */
  onMainMap: () => void;
  /** Hide the "Main map" button when already on the root scene. */
  showMainMap?: boolean;
};

/**
 * Tabbed menu (design 2865:38574): «Hajj Journey» (main scenes + inside-scene
 * checkpoint rows) and «Historical Scenes» (era cards incl. "Kaaba today").
 * Each tab has its own POI-aggregate progress header; every card and
 * inside-scene row navigates on click.
 *
 * Two layouts, picked by `useLayoutMode()` (JS, never `@media` — so the
 * desktop layout never shows inside the mobile-preview frame):
 *   - mobile: full light-gray overlay — close row, tab bar, then a single
 *     scroll column with a sticky "Main map" footer.
 *   - desktop: the SAME single column (480px), centered over a dark scrim,
 *     with the close button beside the column's top edge.
 *
 * The Historical tab is hidden entirely (no tab bar) when no menu scene has
 * a historical family (`resolveHistoricalFamily` returns null for all of them).
 */
export const MenuOverlay: FC<MenuOverlayProps> = ({
  open,
  onClose,
  onEnterMainScene,
  onEnterInsideScene,
  onEnterHistoricalScene,
  onMainMap,
  showMainMap = true,
}) => {
  const i18n = useI18n();
  const isDesktop = useLayoutMode() === 'desktop';
  const { progress, hajj, historical, visitedInside } = useUnit({
    progress: $sceneProgressByPoiFilterId,
    hajj: $hajjJourneyProgress,
    historical: $historicalJourneyProgress,
    visitedInside: $visitedInsideScenes,
  });

  const hasHistoricalTab = MENU_HISTORICAL_SLIDES.length > 0;
  const [tab, setTab] = useState<MenuTab>('hajj');
  const activeTab: MenuTab = hasHistoricalTab ? tab : 'hajj';

  // Every open starts on the default tab.
  useEffect(() => {
    if (open) setTab('hajj');
  }, [open]);

  // User-driven tab switch (the reset-on-open above doesn't track).
  const switchTab = (next: MenuTab) => {
    if (next === activeTab) return;
    menuTabSwitched({ tab: next });
    setTab(next);
  };

  const spotsText = (p?: SceneProgress): string | null =>
    p?.hasData
      ? formatMessage(i18n.menu.spotsCount, { visited: p.visited, total: p.total })
      : null;

  const header = (title: string, aggregate: MenuPoiProgress): ReactNode => (
    <MenuProgressHeader
      title={title}
      percentLabel={aggregate.hasData ? `${aggregate.percent}%` : '—'}
      percent={aggregate.percent}
      caption={formatMessage(i18n.menu.poisProgress, {
        visited: aggregate.visited,
        total: aggregate.total,
      })}
      hasData={aggregate.hasData}
    />
  );

  const hajjCards = MENU_MAIN_SCENE_URL_IDS.map((urlId) => {
    const poiFilterId = SCENE_POI_ID[urlId] ?? urlId;
    const name = i18n.globalMap.markerLabels[urlId] ?? urlId;
    const insideScenes: InsideSceneItem[] = getInsideScenesForParent(urlId).map((inside) => ({
      id: inside.id,
      name: i18n.menu.insideSceneLabels[inside.id] ?? inside.id,
      visited: visitedInside.has(inside.id),
    }));

    // Дизайн-правило: каждая inside-сцена = +1 псевдо-POI в счётчике
    // родительской карточки (visited — когда сцену посещали в этой сессии).
    const p = progress[poiFilterId];
    const cardProgress: SceneProgress | undefined = p?.hasData
      ? {
          visited: p.visited + insideScenes.filter((inside) => inside.visited).length,
          total: p.total + insideScenes.length,
          hasData: true,
        }
      : p;

    return (
      <MenuSceneCard
        key={urlId}
        name={name}
        photoSrc={`/menu/${urlId}.jpg`}
        photoAlt={name}
        exploreLabel={i18n.menu.exploreScene}
        spotsText={spotsText(cardProgress)}
        insideScenes={insideScenes}
        onEnter={() => {
          menuSceneSelected({ targetSceneId: urlId, tab: 'hajj', targetKind: 'main' });
          onEnterMainScene(urlId);
        }}
        onEnterInside={(insideId) => {
          menuSceneSelected({ targetSceneId: insideId, tab: 'hajj', targetKind: 'inside' });
          onEnterInsideScene(insideId);
        }}
      />
    );
  });

  const historicalCards = MENU_HISTORICAL_SLIDES.map((slide) => {
    const era = i18n.historicalScenes.eras[slide.labelKey];
    const name = era?.title ?? slide.labelKey;
    const isEra = isEraScene(slide.sceneId);
    return (
      <MenuSceneCard
        key={slide.sceneId}
        name={name}
        photoSrc={slide.preview}
        photoAlt={name}
        exploreLabel={i18n.menu.exploreScene}
        spotsText={spotsText(progress[SCENE_POI_ID[slide.sceneId] ?? slide.sceneId])}
        // Same gate as the slider card — eras with no scene yet stay inert.
        isLocked={isStubEraScene(slide.sceneId)}
        comingSoonLabel={i18n.historicalScenes.comingSoon}
        onEnter={() => {
          menuSceneSelected({
            targetSceneId: slide.sceneId,
            tab: 'historical',
            targetKind: isEra ? 'era' : 'main',
          });
          // «Kaaba today» — это основная сцена: обычный main-путь. Эпохи идут
          // через историческую навигацию (push / replace / goBack к «сегодня»).
          if (isEra) onEnterHistoricalScene(slide.sceneId);
          else onEnterMainScene(slide.sceneId);
        }}
      />
    );
  });

  const closeButton = (
    <Button
      onClick={onClose}
      className={isDesktop ? cx(styles.closeButton, styles.closeButtonDesktop) : styles.closeButton}
      aria-label={i18n.menu.closeMenu}
    >
      <CloseIcon />
    </Button>
  );

  const tabs = hasHistoricalTab ? (
    <div className={styles.tabs} role="tablist">
      <UnstyledButton
        role="tab"
        aria-selected={activeTab === 'hajj'}
        className={cx(styles.tab, activeTab === 'hajj' && styles.tabActive)}
        onClick={() => switchTab('hajj')}
      >
        {i18n.menu.tabHajj}
      </UnstyledButton>
      <UnstyledButton
        role="tab"
        aria-selected={activeTab === 'historical'}
        className={cx(styles.tab, activeTab === 'historical' && styles.tabActive)}
        onClick={() => switchTab('historical')}
      >
        {i18n.menu.tabHistorical}
      </UnstyledButton>
    </div>
  ) : null;

  const mainMapButton = showMainMap ? (
    <UnstyledButton className={styles.mainMapPill} onClick={onMainMap}>
      <MapTrifoldIcon />
      <span>{i18n.menu.mainMap}</span>
    </UnstyledButton>
  ) : null;

  // `key={activeTab}` remounts the scroll container on tab switch so the
  // new tab always starts at the top.
  const scroll = (
    <div className={styles.scroll} key={activeTab}>
      {activeTab === 'hajj' ? (
        <>
          {header(i18n.menu.hajjProgressTitle, hajj)}
          {hajjCards}
        </>
      ) : (
        <>
          {header(i18n.menu.historicalProgressTitle, historical)}
          {historicalCards}
        </>
      )}
      {mainMapButton && <div className={styles.mainMapWrap}>{mainMapButton}</div>}
    </div>
  );

  return (
    <div
      className={cx(styles.overlay, isDesktop && styles.overlayDesktop, !open && styles.hidden)}
      aria-hidden={!open}
    >
      {isDesktop ? (
        <div className={styles.desktopContent}>
          {closeButton}
          <div className={styles.desktopColumn}>
            {tabs}
            {scroll}
          </div>
        </div>
      ) : (
        <>
          <div className={styles.header}>{closeButton}</div>
          {tabs && <div className={styles.tabsRow}>{tabs}</div>}
          {scroll}
        </>
      )}
    </div>
  );
};
